import type { LucideIcon } from "lucide-react";
import { MODULE_ICONS, type ModuleCode } from "./modules";

// Per-module visual identity. One source of truth for the colour
// + icon used across AuthoringList rows, SittingsList cards, the
// SittingHub student slots, and the module-specific section cards
// in each sub-editor.
//
// The classes are intentionally tinted, not saturated — the goal
// is a calm "this is a Reading thing" cue, not a bright label.
// Light + dark variants supplied so the same class works under
// the user's preferred theme.

export type ModulePaletteToken = {
  /** Lucide icon component for the module. Re-exported from MODULE_ICONS. */
  icon: LucideIcon;
  /** Border colour for a card / chip outline (left-edge accent bar). */
  accentBorder: string;
  /** `border-l-<colour>` variant for cards that use a left-edge accent.
   *  MUST be a literal class string so Tailwind's JIT scanner picks it
   *  up at build time — never construct this from accentBorder at runtime. */
  accentBorderLeft: string;
  /** Solid colour for the 3-4 px left-edge accent bar on a card. */
  accentBar: string;
  /** Soft tinted background used inside the icon chip / hover surfaces. */
  accentBg: string;
  /** Foreground colour for module-tinted text + icons. */
  accentText: string;
  /** Compact ring / focus colour for keyboard focus states. */
  accentRing: string;
};

// Tailwind palette mapping. Each module gets a distinct hue so the
// eye triages a list of mixed-module rows without reading labels:
//   Reading      → blue       (calm reading-passage feel)
//   Writing      → purple
//   Listening    → amber      (warm; audio context)
//   Speaking     → fuchsia    (matches the speaking-prompt block)
//   HEDA English → teal
//   HEDA Math    → emerald
//   HEDA ICT     → slate
const TOKENS: Record<ModuleCode, Omit<ModulePaletteToken, "icon">> = {
  reading: {
    accentBorder: "border-blue-400/50",
    accentBorderLeft: "border-l-blue-400/50",
    accentBar: "bg-blue-500",
    accentBg: "bg-blue-100 dark:bg-blue-950/40",
    accentText: "text-blue-700 dark:text-blue-300",
    accentRing: "ring-blue-400/60",
  },
  writing: {
    accentBorder: "border-purple-400/50",
    accentBorderLeft: "border-l-purple-400/50",
    accentBar: "bg-purple-500",
    accentBg: "bg-purple-100 dark:bg-purple-950/40",
    accentText: "text-purple-700 dark:text-purple-300",
    accentRing: "ring-purple-400/60",
  },
  listening: {
    accentBorder: "border-amber-400/50",
    accentBorderLeft: "border-l-amber-400/50",
    accentBar: "bg-amber-500",
    accentBg: "bg-amber-100 dark:bg-amber-950/40",
    accentText: "text-amber-700 dark:text-amber-300",
    accentRing: "ring-amber-400/60",
  },
  speaking: {
    accentBorder: "border-fuchsia-400/50",
    accentBorderLeft: "border-l-fuchsia-400/50",
    accentBar: "bg-fuchsia-500",
    accentBg: "bg-fuchsia-100 dark:bg-fuchsia-950/40",
    accentText: "text-fuchsia-700 dark:text-fuchsia-300",
    accentRing: "ring-fuchsia-400/60",
  },
  heda_english: {
    accentBorder: "border-teal-400/50",
    accentBorderLeft: "border-l-teal-400/50",
    accentBar: "bg-teal-500",
    accentBg: "bg-teal-100 dark:bg-teal-950/40",
    accentText: "text-teal-700 dark:text-teal-300",
    accentRing: "ring-teal-400/60",
  },
  heda_math: {
    accentBorder: "border-emerald-400/50",
    accentBorderLeft: "border-l-emerald-400/50",
    accentBar: "bg-emerald-500",
    accentBg: "bg-emerald-100 dark:bg-emerald-950/40",
    accentText: "text-emerald-700 dark:text-emerald-300",
    accentRing: "ring-emerald-400/60",
  },
  heda_ict: {
    accentBorder: "border-slate-400/50",
    accentBorderLeft: "border-l-slate-400/50",
    accentBar: "bg-slate-500",
    accentBg: "bg-slate-200 dark:bg-slate-800",
    accentText: "text-slate-700 dark:text-slate-300",
    accentRing: "ring-slate-400/60",
  },
};

// Generic fallback used when a row comes through with an unknown
// module code (e.g. a new module added on the server before the
// web build catches up).
const FALLBACK: Omit<ModulePaletteToken, "icon"> = {
  accentBorder: "border-border",
  accentBorderLeft: "border-l-border",
  accentBar: "bg-muted-foreground",
  accentBg: "bg-muted",
  accentText: "text-muted-foreground",
  accentRing: "ring-border",
};

export function modulePalette(moduleCode: string): ModulePaletteToken {
  const tokens = TOKENS[moduleCode as ModuleCode] ?? FALLBACK;
  const icon =
    MODULE_ICONS[moduleCode as ModuleCode] ??
    MODULE_ICONS["reading"]; // BookOpen as the generic fallback icon
  return { ...tokens, icon };
}
